import React, {useEffect, useState} from "react";
import {Badge, Button, Card, CardBody, CardHeader, CardText, CardTitle} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import {IOrderRule, IOrderRuleBlock} from "../../../../../store/trafficManagement/orderRules/types";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {OrderRuleService} from "../../../../../services/order_rule.service";
import usePermissions from "../../../../../hooks/usePermissions";

interface CardOrderRuleProps {
    orderRule: IOrderRule
    onEdit?: (id: number) => void;
    invalidateOrderRules?: (type?: "edit" | "remove") => Promise<void>;
}

const toLabel = (key: string): string => {
    return key.replace(/_/g, " ")
        .replace(/\b\w/g, char => char.toUpperCase());
}

const formatOrderRuleBlock = (block?: IOrderRuleBlock): string => {
    if (!block) return "—";

    const {type, value} = block;

    switch (type) {
        case "number":
            return String(value ?? "—");

        case "string":
            return (value as string) ?? "—";

        case "boolean":
            return value ? "Yes" : "No";

        case "array": {
            const arr = Array.isArray(value) ? value : [];
            if (arr.length === 0) return "—";

            const first = arr[0];
            if (first && typeof first === "object") {
                return (arr as any[])
                    .map(item => (item?.name ?? item?.label ?? JSON.stringify(item)))
                    .join(", ");
            }

            return arr.map(String).join(", ");
        }

        case "object":
            return typeof value === "object" && value !== null ? JSON.stringify(value) : String(value);

        default:
            return "Unsupported";
    }
}

const CardOrderRule: React.FC<CardOrderRuleProps> = ({orderRule, onEdit, invalidateOrderRules}) => {
    const queryClient = useQueryClient();
    const {hasPermission} = usePermissions();

    const [toggleStatusApprove, setToggleStatusApprove] = useState<boolean>(false);
    const [removeApprove, setRemoveApprove] = useState<boolean>(false);
    const [sureToRemove, setSureToRemove] = useState<boolean>(false);

    const onSuccess = (type?: "edit" | "remove") => {
        if (!invalidateOrderRules) return;

        invalidateOrderRules(type).then(() => {
            setToggleStatusApprove(false);
            setRemoveApprove(false);
        });
    }

    const {mutate: toggleActive, isPending: isPendingToggle} = useMutation({
        mutationFn: (ruleId: number) => OrderRuleService.toggleStatusRule(ruleId),
        onSuccess: () => onSuccess("edit"),
        onError: () => setToggleStatusApprove(false),
        mutationKey: ["keyToggleRuleActive"],
    });

    const {mutate: removeRule, isPending: isPendingRemove} = useMutation({
        mutationFn: (ruleId: number) => OrderRuleService.removeRule(ruleId),
        onSuccess: () => onSuccess("remove"),
        onError: () => setRemoveApprove(false),
        mutationKey: ["keyRemoveRule"],
    });

    const handleToggleClick = () => {
        setToggleStatusApprove(true);
        toggleActive(orderRule.id)
    }

    const handleEditClick = () => {
        if (onEdit) onEdit(orderRule.id)
    }

    const handleRemoveClick = () => {
        if (!sureToRemove) {
            setSureToRemove(true)
            return;
        }

        setSureToRemove(false)
        setRemoveApprove(true)
        removeRule(orderRule.id)
    }

    useEffect(() => {
        if (sureToRemove) {
            const timer = setTimeout(() => setSureToRemove(false), 2500);
            return () => clearTimeout(timer);
        }
    }, [sureToRemove]);

    const getRuleStatus = (isActive: boolean, isPending: boolean) => {
        if (isPending)
            return {statusColor: "secondary", statusLabel: "..."};

        return isActive ?
            {statusColor: "success", statusLabel: "Active"} :
            {statusColor: "danger", statusLabel: "Inactive"};
    }

    const isPendingToggleFull = (isPendingToggle || !!queryClient.isFetching({queryKey: ["keyRuleList"]})) && toggleStatusApprove;
    const isPendingRemoveFull = (isPendingRemove || !!queryClient.isFetching({queryKey: ["keyRuleList"]})) && removeApprove;

    const {statusColor, statusLabel} = getRuleStatus(orderRule.active, isPendingToggleFull);

    return (
        <Card className={isPendingRemoveFull ? "opacity-50 pe-none" : undefined}>
            <CardHeader className="p-3 pt-2">
                <Button color="link" className="p-0 border-0" onClick={handleToggleClick}>
                    <Badge color={statusColor} className="d-inline-flex align-items-center gap-1">
                        {statusLabel}
                    </Badge>
                </Button>

                <div className="d-flex align-items-center justify-content-between">
                    <CardTitle tag="h5" className="fs-5 m-0">
                        #{orderRule.id} | {orderRule.name}
                    </CardTitle>

                    <div className="d-flex align-items-center gap-1">
                        {hasPermission('update-traffic-rules') &&
                            <Button className="d-flex p-1" color="success" onClick={handleEditClick}
                                    disabled={!hasPermission('update-traffic-rules')}>
                                <FeatherIcon icon="edit-2" size={16}/>
                            </Button>}

                        {hasPermission('delete-traffic-rules') &&
                            <Button className="d-flex p-1" color="danger" onClick={handleRemoveClick}
                                    disabled={!hasPermission('delete-traffic-rules')}>
                                {sureToRemove ?
                                    <FeatherIcon icon="check" size={16}/> :
                                    <FeatherIcon icon="trash-2" size={16}/>}
                            </Button>}
                    </div>
                </div>
            </CardHeader>
            <CardBody className="p-3">
                <div className="d-flex pe-2">
                    <div className="flex-shrink-0 border-end pe-3">
                        <CardTitle tag="h2" className="m-0">
                            Method:
                        </CardTitle>
                        <CardText className="text-capitalize">
                            {orderRule.method.split("--").join(" ")}
                        </CardText>
                    </div>

                    <div className="ps-2">
                        {Object.entries(orderRule.rule).map(([key, rule]) => (
                            <div key={key} className="mb-2">
                                <CardTitle tag="h2" className="m-0">
                                    {toLabel(key)}:
                                </CardTitle>
                                <CardText className="text-wrap">
                                    {formatOrderRuleBlock(rule)}
                                </CardText>
                            </div>
                        ))}
                    </div>
                </div>
            </CardBody>
        </Card>
    )
}

export default CardOrderRule